import { useState } from 'react';
import { Alert } from "antd";
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, PageHeader, Spinner } from '@shared/ui';
import { ApiError } from '@shared/api';
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsInfiniteQuery,
  type NotificationView,
} from '@entities/notification';
import { NotificationItem } from './notification-item';

export function NotificationListPage() {
  const { t } = useTranslation();
  const query = useNotificationsInfiniteQuery();
  const markRead = useMarkNotificationReadMutation();
  const markAllMutation = useMarkAllNotificationsReadMutation();
  const [bulkError, setBulkError] = useState<string | null>(null);

  const items: NotificationView[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = items.filter((n) => !n.read);

  const markAll = () => {
    setBulkError(null);
    markAllMutation.mutate(undefined, {
      onError: (err) =>
        setBulkError(err instanceof ApiError ? err.message : 'Failed to mark all'),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('notifications.title')}
        subtitle={t('notifications.subtitle')}
        extra={
          unread.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              loading={markAllMutation.isPending}
              onClick={markAll}
            >
              {t('notifications.markAllRead', { count: unread.length })}
            </Button>
          ) : null
        }
      />

      {bulkError ? <Alert type="error" showIcon message={bulkError} /> : null}

      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : query.isError ? (
        <p>{t('products.loadFail', { message: query.error?.message ?? 'unknown' })}</p>
      ) : items.length === 0 ? (
        <EmptyState
          title={t('notifications.empty.title')}
          description={t('notifications.empty.description')}
        />
      ) : (
        <div className="flex flex-col gap-2.5">
          {items.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onAfterAction={() => {
                if (!n.read) markRead.mutate(n.id);
              }}
            />
          ))}
          {query.hasNextPage ? (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common.loadOlder')}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
